import React from 'react';

import { Alert, Box } from '@mui/material';
import { ContainerYieldProvider } from '@wp-block-builder/blocks/ContainerYield';
import { type TBlock } from '@wp-block-builder/package';
import { BRAND_RED } from '@wp-constants/colors';

import { WpRendererError } from '@waypoint/templates';

import ReadOnlyBlock from '../../ReadOnlyMessage/Block';

import LayoutReadOnlySection from './LayoutReadOnlySection';

type RootEditableBlockProps = {
  layoutBlocks: TBlock[];
  variables: Record<string, unknown>;
  children: JSX.Element;
};
type TState = {
  error: WpRendererError | null;
};
export default class LayoutHandler extends React.Component<RootEditableBlockProps, TState> {
  state: TState = { error: null };

  componentDidCatch(error: unknown) {
    if (error instanceof WpRendererError) {
      this.setState({ error });
    }
  }

  componentDidUpdate(prevProps: Readonly<RootEditableBlockProps>): void {
    if (!this.state.error) {
      return;
    }
    const { layoutBlocks, variables } = this.props;
    if (prevProps.layoutBlocks !== layoutBlocks || prevProps.variables !== variables) {
      this.setState({ error: null });
    }
  }

  render() {
    const { children, layoutBlocks } = this.props;
    const error = this.state.error;

    if (error) {
      return (
        <Box
          sx={{
            position: 'relative',
            '&::after': {
              outline: `1px solid ${BRAND_RED}`,
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              content: `""`,
              pointerEvents: 'none',
              zIndex: 5,
            },
          }}
        >
          <Alert variant="filled" severity="error">
            Error in layout: {error.message}
          </Alert>
          {children}
        </Box>
      );
    }

    return (
      <ContainerYieldProvider yieldContent={children}>
        {layoutBlocks.map((block) => {
          if (block.type === 'ContainerYield') {
            return <ReadOnlyBlock block={block} />;
          }
          return <LayoutReadOnlySection key={block.id} block={block} />;
        })}
      </ContainerYieldProvider>
    );
  }
}
